import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from './auth.service';
import { filter } from 'rxjs';

/**
 * Monitors route navigation and logs suspicious access patterns.
 * Detects unauthorized attempts to access admin/super-admin routes.
 * 
 * This provides an audit trail of suspicious navigation on the client
 * (complementing server-side access logging).
 */
@Injectable({
  providedIn: 'root'
})
export class RouteSecurityService {

  private readonly SENSITIVE_ROUTES = [
    '/admin',
    '/super-admin',
    '/backoffice',
    '/admin-management',
    '/news-management',
    '/library-management',
    '/announcements-management',
    '/partner-logos-management',
    '/support-management',
    '/home-banners-management'
  ];

  private suspiciousAttempts = 0;
  private readonly MAX_SUSPICIOUS_BEFORE_LOGOUT = 5;

  constructor(
    private router: Router,
    private authService: AuthService
  ) {
    this.monitorNavigation();
  }

  private monitorNavigation(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event) => {
      const navEvent = event as NavigationEnd;
      this.checkSuspiciousAccess(navEvent.urlAfterRedirects || navEvent.url);
    });
  }

  private checkSuspiciousAccess(url: string): void {
    const isSensitiveRoute = this.SENSITIVE_ROUTES.some(route => url.startsWith(route));

    if (!isSensitiveRoute) return;

    const user = this.authService.user;

    // Not authenticated but trying to access sensitive route
    if (!this.authService.isAuthenticated) {
      this.suspiciousAttempts++;
      this.logSecurityEvent('UNAUTHENTICATED_SENSITIVE_ACCESS', url, null);
    }
    // Regular user trying to access admin routes
    else if (user && user.role_id === 1) {
      if (url.startsWith('/admin') || url.startsWith('/super-admin') || url.startsWith('/backoffice')) {
        this.suspiciousAttempts++;
        this.logSecurityEvent('UNAUTHORIZED_ROLE_ACCESS', url, user.user_id);
      }
    }

    // Force logout after too many suspicious attempts
    if (this.suspiciousAttempts >= this.MAX_SUSPICIOUS_BEFORE_LOGOUT) {
      this.authService.logout();
      this.router.navigate(['/login']);
      this.suspiciousAttempts = 0;
    }
  }

  private logSecurityEvent(type: string, url: string, userId: string | null): void {
    const event = {
      type,
      url,
      userId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    // Store locally for potential backend sync
    try {
      const events = JSON.parse(sessionStorage.getItem('security_events') || '[]');
      events.push(event);
      // Keep only last 20 events
      if (events.length > 20) events.shift();
      sessionStorage.setItem('security_events', JSON.stringify(events));
    } catch { /* storage disabled */ }
  }
}
